import { useCallback, useEffect, useMemo, useState } from 'react';
import { useShell } from '../../app/shell-context';
import { ProgressRing } from '../../components/ProgressRing';
import { AcademicCourse, AcademicProfile as AcademicProfileData, api, Viewer } from '../../lib/api';
import { GoalPlanner } from './GoalPlanner';
import { ManualAcademicData } from './ManualAcademicData';

const WEEKDAYS = ['', '周一', '周二', '周三', '周四', '周五', '周六', '周日'];

interface CourseSummary {
  key: string;
  name: string;
  code?: string;
  teacher?: string;
  term?: string;
  slots: AcademicCourse[];
}

function summarizeCourses(courses: AcademicCourse[]) {
  const summaries = new Map<string, CourseSummary>();
  for (const course of courses) {
    const key = course.courseCode || course.courseName;
    const current = summaries.get(key) || { key, name: course.courseName, code: course.courseCode, teacher: course.teacher, term: course.term, slots: [] };
    if (course.weekday && course.startPeriod) current.slots.push(course);
    if (!current.teacher && course.teacher) current.teacher = course.teacher;
    summaries.set(key, current);
  }
  return [...summaries.values()];
}

function termLabel(term: string) {
  const match = term.match(/(\d{4})-(\d{4})-(\d)/);
  return match ? `${match[1].slice(2)}/${match[3] === '1' ? '秋' : '春'}` : term;
}

function TrendChart({ profile }: { profile: AcademicProfileData }) {
  const points = profile.gpaTrend.filter((item) => item.gpa !== null);
  if (!points.length) return <div className="academic-empty-chart"><span>绩</span><p>成绩数据尚未连接，接入后这里会按学期绘制真实趋势。</p></div>;
  const width = 520;
  const height = 190;
  const values = points.map((item) => item.gpa as number);
  const min = Math.max(0, Math.min(...values) - .35);
  const max = Math.min(5, Math.max(...values) + .35);
  const range = Math.max(.5, max - min);
  const coordinates = points.map((item, index) => ({
    ...item,
    x: points.length === 1 ? width / 2 : 25 + index * (width - 50) / (points.length - 1),
    y: 20 + (max - (item.gpa as number)) / range * (height - 55),
  }));
  const line = coordinates.map((point, index) => `${index ? 'L' : 'M'}${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join(' ');
  return <div className="academic-trend-chart">
    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="各学期绩点趋势">
      <path className="trend-area" d={`${line} L${coordinates.at(-1)?.x} ${height - 24} L${coordinates[0].x} ${height - 24} Z`} />
      <path className="trend-line" d={line} />
      {coordinates.map((point) => <g key={point.term}><circle cx={point.x} cy={point.y} r="5" /><text x={point.x} y={point.y - 12}>{point.gpa?.toFixed(2)}</text><text className="term" x={point.x} y={height - 6}>{termLabel(point.term)}</text></g>)}
    </svg>
  </div>;
}

function FiveEducation({ profile }: { profile: AcademicProfileData }) {
  const values = profile.youth?.fiveEducation?.five || {};
  const items = ['德', '智', '体', '美', '劳'].map((name) => ({ name, value: Number(values[name] || 0) }));
  const hasData = items.some((item) => item.value > 0);
  const max = Math.max(1, ...items.map((item) => item.value));
  return <section className="paper-card academic-panel five-education-panel">
    <header><div><small>FIVE EDUCATION · ROOT CROWN</small><h3>五育根冠</h3></div><span>{hasData ? `${items.reduce((sum, item) => sum + item.value, 0)} 项记录` : '等待数据'}</span></header>
    {hasData ? <div className="five-education-bars">{items.map((item) => <div key={item.name}><i style={{ height: `${Math.max(8, item.value / max * 100)}%` }} /><strong>{item.value}</strong><span>{item.name}</span></div>)}</div> : <div className="academic-empty compact"><span>育</span><p>五育数据尚未采集；页面不会用占位分数代替。</p></div>}
    <footer><span>志愿时长 <b>{profile.youth?.volunteerHours ?? '—'}</b> h</span><span>参与活动 <b>{profile.youth?.activityCount ?? '—'}</b></span></footer>
  </section>;
}

function AcademicDiagnosis({ profile }: { profile: AcademicProfileData }) {
  const weak = profile.weak || [];
  const requirements = profile.planBreakdown || [];
  const earned = profile.earnedByType || [];
  return <section className="paper-card academic-panel diagnosis-panel">
    <header><div><small>DIAGNOSIS · CREDIT MAP</small><h3>学业诊断与学分结构</h3></div><span>{weak.length ? `${weak.length} 门需留意` : '暂无低分课程'}</span></header>
    <div className="diagnosis-grid">
      <div><h4>薄弱课程</h4>{weak.length ? <ul>{weak.map((course) => <li key={`${course.term}-${course.name}`}><span><b>{course.name || '未命名课程'}</b><small>{course.term || '学期未标注'} · {course.credit ?? '—'} 学分</small></span><em>{course.score ?? '—'}</em></li>)}</ul> : <p className="diagnosis-empty">已录入成绩中没有低于 75 分的课程。</p>}</div>
      <div><h4>培养方案结构</h4>{requirements.length || earned.length ? <dl>{requirements.map((item) => { const matched = earned.find((record) => item.group?.includes(record.type || '') || record.type?.includes(item.group || '')); return <div key={item.group}><dt>{item.group || '课程类别'}</dt><dd><b>{matched?.credits ?? 0}</b><small> / {item.required ?? '—'} 学分</small></dd></div>; })}{!requirements.length ? earned.map((item) => <div key={item.type}><dt>{item.type || '未标注'}</dt><dd><b>{item.credits ?? 0}</b><small> 学分</small></dd></div>) : null}</dl> : <p className="diagnosis-empty">补全培养方案或成绩类别后，这里会显示分类学分差距。</p>}</div>
    </div>
  </section>;
}

export function AcademicProfile({ displayName, profileContext, writeAllowed }: { displayName?: string; profileContext?: Viewer['profile']; writeAllowed: boolean }) {
  const { openDock } = useShell();
  const [profile, setProfile] = useState<AcademicProfileData>();
  const [error, setError] = useState('');
  const courses = useMemo(() => summarizeCourses(profile?.currentCourses || []), [profile?.currentCourses]);

  const loadProfile = useCallback(async () => {
    try {
      const response = await api<{ data: AcademicProfileData }>('/api/v1/profile/academic');
      setProfile(response.data); setError('');
    } catch (requestError) { setError(requestError instanceof Error ? requestError.message : '成长画像加载失败'); }
  }, []);
  const refreshAfterWrite = useCallback(() => {
    window.dispatchEvent(new Event('caigen:academic-updated'));
    void loadProfile();
  }, [loadProfile]);

  useEffect(() => { void loadProfile(); }, [loadProfile]);

  useEffect(() => {
    if (!profile || window.location.hash !== '#academic-profile') return;
    const frame = window.requestAnimationFrame(() => document.getElementById('academic-profile')?.scrollIntoView({ block: 'start' }));
    return () => window.cancelAnimationFrame(frame);
  }, [profile]);

  if (!profile) return <section className="paper-card academic-loading" id="academic-profile"><span>脉</span><p>{error || '正在整理成长画像…'}</p></section>;
  const creditProgress = profile.credits.progress ?? 0;
  const hasGrades = profile.gpa !== null;

  return <section className="academic-profile" id="academic-profile" aria-labelledby="academic-title">
    <header className="academic-section-title"><div><small>GROWTH · ACADEMIC CONTEXT</small><h2 id="academic-title">成长画像</h2><p>把成绩、培养方案、当前课程与五育记录放在同一条根脉上。</p></div><button type="button" onClick={() => openDock({ kind: 'agent', prompt: '请根据我的真实学术画像，分析当前学习进度、课程负荷和下一步重点。不要猜测缺失数据。' })}>请菜根分析</button></header>

    <section className="academic-hero paper-card">
      <div className="academic-hero-copy"><span>培养进度</span><h3>{profile.plan?.name || profile.plan?.major || '个人培养档案'}</h3><p>{profile.plan ? [profile.plan.grade, profile.plan.major].filter(Boolean).join(' · ') : '培养方案尚未连接，已获得学分仍按成绩记录计算。'}</p></div>
      <ProgressRing value={creditProgress} label="学分完成" detail={profile.credits.required ? `${profile.credits.earned}/${profile.credits.required}` : `${profile.credits.earned} 已获`} tone="wisteria" />
      <dl><div><dt>学分绩</dt><dd>{hasGrades ? profile.gpa?.toFixed(2) : '—'}<small> / 5.00</small></dd></div><div><dt>加权均分</dt><dd>{profile.average?.toFixed(2) || '—'}<small> / 100</small></dd></div><div><dt>成绩课程</dt><dd>{profile.courseCount}<small> 门</small></dd></div></dl>
      <i className="academic-root-thread" aria-hidden="true" />
    </section>

    <div className="academic-evidence-strip" aria-label="画像依据">
      <span><small>专业排名</small><b>{profile.identity?.rankPercent != null ? `前 ${profile.identity.rankPercent}%` : '待补全'}</b></span>
      <span><small>英语能力</small><b>{profile.identity?.cet6 ? `CET-6 ${profile.identity.cet6}` : profile.identity?.cet4 ? `CET-4 ${profile.identity.cet4}` : '待补全'}</b></span>
      <span><small>不及格课程</small><b>{profile.failed?.count ?? '—'} 门</b></span>
      <span><small>画像跨度</small><b>{profile.termsCount || profile.gpaTrend.length || '—'} 学期</b></span>
      <span><small>体测状态</small><b>{profile.identity?.physicalTest === 'pass' ? '达标' : profile.identity?.physicalTest === 'fail' ? '未达标' : '待补全'}</b></span>
      <span><small>行为考评</small><b>{profile.identity?.dailyBehavior === 'excellent' ? '优秀' : profile.identity?.dailyBehavior === 'good' ? '良好' : profile.identity?.dailyBehavior === 'qualified' ? '合格' : '待补全'}</b></span>
    </div>

    <section className="paper-card self-portrait-panel">
      <header><div><small>SELF REPORT · LEARNING PORTRAIT</small><h3>学习自述</h3></div><a href="#personal-profile">去更新</a></header>
      <div><span><small>兴趣方向</small>{profileContext?.interests?.length ? <p>{profileContext.interests.map((item) => <i key={item}>{item}</i>)}</p> : <b>还没有记录兴趣方向</b>}</span><span><small>需要加强</small>{profileContext?.weakSubjects?.length ? <p>{profileContext.weakSubjects.map((item) => <i key={item}>{item}</i>)}</p> : <b>还没有记录薄弱科目</b>}</span></div>
    </section>

    <GoalPlanner writeAllowed={writeAllowed} onSaved={refreshAfterWrite} />

    <div className="academic-grid">
      <section className="paper-card academic-panel trend-panel"><header><div><small>TERM · TREND</small><h3>学期绩点趋势</h3></div><span>{hasGrades ? '南大 5 分制' : '未连接成绩'}</span></header><TrendChart profile={profile} /></section>
      <FiveEducation profile={profile} />
    </div>

    <AcademicDiagnosis profile={profile} />

    <section className="paper-card academic-panel course-panel">
      <header><div><small>COURSE · CURRENT TERM</small><h3>本学期课程</h3></div><span>{courses.length} 门课程 · {profile.currentCourses.filter((item) => item.weekday).length} 个固定时段</span></header>
      {courses.length ? <div className="academic-course-list">{courses.map((course) => <article key={course.key}><span>{course.code || '课程'}</span><div><h4>{course.name}</h4><p>{[course.teacher, course.term].filter(Boolean).join(' · ') || '课程资料已连接'}</p>{course.slots.length ? <small>{course.slots.map((slot) => `${WEEKDAYS[slot.weekday || 0]} ${slot.startPeriod}-${slot.endPeriod}节${slot.location ? ` · ${slot.location}` : ''}`).join('；')}</small> : <small>没有固定排课时段</small>}</div></article>)}</div> : <div className="academic-empty"><span>课</span><p>还没有课程数据。可前往日历使用“导入课表”。</p><a href="/calendar">前往日历</a></div>}
    </section>
    <ManualAcademicData profile={profile} displayName={displayName} writeAllowed={writeAllowed} onSaved={refreshAfterWrite} />
    <p className="academic-footnote">画像只使用服务端已有数据；缺失项明确留空，不用推测值填补。目标判断是辅助分析，正式评奖与毕业条件仍以学校当期文件为准。</p>
  </section>;
}
